// This hook returns wrappers around react-router-dom v6 exports

// It accepts an optional searchParamTypes object to define the types
//   used when returning those props in the searchParamsObject

// It returns an array of two items
// - first item is an object representing searchParams, deconstruct
//   this object to retrieve search params converted to the specified type.
//
// - second item is an object with data from wrapper methods in this hook. These
//   should be used instead of importing 'react-router-dom' in components

import { useMemo } from 'react';

import {
  NavigationContextValue,
  useNavigationContext,
} from '@atom/components/NavigationProvider';

export enum SearchParamType {
  NUMBER = 'NUMBER',
  ARRAY = 'ARRAY',
  BOOLEAN = 'BOOLEAN',
  STRING = 'STRING',
}

interface SearchTypesObject {
  [key: string]: SearchParamType;
}

interface SearchParamsObject {
  [key: string]: string & boolean & number & string[];
}

type NavigationData = [SearchParamsObject, NavigationContextValue];

export const useNavigation = (
  searchParamTypes: SearchTypesObject = {},
): NavigationData => {
  const context = useNavigationContext();
  const { searchParams } = context;

  const searchParamsObject: SearchParamsObject = useMemo(() => {
    const paramsObject = {};
    searchParams.forEach((value, key) => {
      const paramType: SearchParamType =
        (searchParamTypes[key] as SearchParamType) || SearchParamType.STRING;
      switch (paramType) {
        case SearchParamType.ARRAY:
          paramsObject[key] = value?.split(',') || [];
          break;
        case SearchParamType.NUMBER:
          paramsObject[key] = Number(value);
          break;
        case SearchParamType.BOOLEAN:
          paramsObject[key] = value === 'true';
          break;
        case SearchParamType.STRING:
          paramsObject[key] = value;
          break;
        default:
          // fallback to string
          paramsObject[key] = value;
          break;
      }
    });
    return paramsObject;
  }, [searchParams]);

  return [searchParamsObject, context];
};
